import React, { useEffect, useState } from 'react';
import { Star, CalendarClock, CheckCircle2, BellRing, TrendingUp, TrendingDown } from 'lucide-react';
import api from '../../lib/api';

const DAY = 86400000;
const SELLER_INACTIVE = ['vendu', 'sold', 'denonce', 'denounced', 'expire', 'expired', 'draft', 'brouillon', 'generated'];

/** Sélectionne le mandat actif le plus ancien (celui qui rythme les évaluations). */
export function getActiveSellerMandate(mandates = []) {
    const active = (mandates || []).filter((m) => !SELLER_INACTIVE.includes(m.status));
    return active
        .slice()
        .sort(
            (a, b) =>
                new Date(a.signed_at || a.start_date || a.created_at) -
                new Date(b.signed_at || b.start_date || b.created_at)
        )[0] || null;
}

function monthsBetween(from, to) {
    let m = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
    if (to.getDate() < from.getDate()) m -= 1;
    return Math.max(0, m);
}

/** Nombre de mois pleins écoulés depuis la signature = index de la période d'évaluation. */
export function getEvalPeriod(mandate) {
    if (!mandate) return 0;
    const start = new Date(mandate.signed_at || mandate.start_date || mandate.created_at);
    if (isNaN(start)) return 0;
    return monthsBetween(start, new Date());
}

function nextAnniversary(start, period) {
    const d = new Date(start);
    d.setMonth(d.getMonth() + period + 1);
    return d;
}

const doneKey = (id) => `homly_seller_eval_done_${id}`;

/** Marque l'évaluation de la période courante comme réalisée. */
export function markEvalDone(mandate) {
    if (!mandate) return;
    try {
        localStorage.setItem(doneKey(mandate.id), String(getEvalPeriod(mandate)));
    } catch {
        /* noop */
    }
}

/** Mini-courbe SVG de l'historique de satisfaction (notes /5, du plus ancien au plus récent). */
function SatisfactionSparkline({ points }) {
    if (!points || points.length < 2) return null;
    const vals = points.map((p) => p.overall);
    const w = 132;
    const h = 30;
    const stepX = w / (vals.length - 1);
    const coords = vals.map((v, i) => {
        const x = i * stepX;
        const y = h - (Math.max(0, Math.min(5, v)) / 5) * (h - 4) - 2;
        return [x, y];
    });
    const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c[0].toFixed(1)} ${c[1].toFixed(1)}`).join(' ');
    const area = `${line} L ${w} ${h} L 0 ${h} Z`;
    const avg = (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
    const up = vals[vals.length - 1] >= vals[0];

    return (
        <div className="mt-3 pt-3 border-t border-border" data-testid="satisfaction-history">
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] text-muted-foreground">Votre satisfaction</span>
                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${up ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {avg}/5
                    <span className="text-muted-foreground font-normal">· {vals.length} éval.</span>
                </span>
            </div>
            <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="overflow-visible">
                <defs>
                    <linearGradient id="satGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FF914D" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#FF914D" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <path d={area} fill="url(#satGrad)" />
                <path d={line} fill="none" stroke="#FF914D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                {coords.map((c, i) => (
                    <circle key={i} cx={c[0]} cy={c[1]} r={i === coords.length - 1 ? 2.6 : 1.6} fill="#FF914D" />
                ))}
            </svg>
        </div>
    );
}

/**
 * Carte d'évaluation pour UN mandat — logique interne.
 * (Garde le même comportement qu'avant : compte à rebours mensuel, CTA "Évaluer
 * maintenant" si due, historique satisfaction.)
 */
function SingleMandateEvalCard({ mandate, onEvaluate }) {
    const [history, setHistory] = useState([]);

    useEffect(() => {
        if (!mandate?.id) return;
        let active = true;
        api.get(`/ratings/mandate/${mandate.id}/history`)
            .then((res) => {
                if (!active) return;
                const evals = (res.data?.evaluations || [])
                    .filter((e) => (e.overall || 0) > 0)
                    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                    .slice(-6);
                setHistory(evals);
            })
            .catch(() => { /* silent */ });
        return () => { active = false; };
    }, [mandate?.id]);

    if (!mandate) return null;

    const start = new Date(mandate.signed_at || mandate.start_date || mandate.created_at);
    const period = getEvalPeriod(mandate);
    const next = nextAnniversary(start, period);
    const daysUntil = Math.max(0, Math.ceil((next - new Date()) / DAY));

    let donePeriod = -1;
    try {
        donePeriod = parseInt(localStorage.getItem(doneKey(mandate.id)) ?? '-1', 10);
    } catch {
        donePeriod = -1;
    }
    const isDue = period >= 1 && donePeriod !== period;
    const agentName = mandate.agent_name || 'votre agent';
    const addressLabel = mandate.property?.address || mandate.address || 'votre bien';
    const dateLabel = next.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    const sparkline = <SatisfactionSparkline points={history} />;
    const testIdSuffix = mandate.id ? `-${mandate.id.slice(0, 8)}` : '';

    if (isDue) {
        return (
            <div
                className="bg-gradient-to-br from-yellow-500/10 to-[#FF914D]/10 rounded-xl border border-yellow-500/30 p-5"
                data-testid={`next-evaluation-card${testIdSuffix}`}
            >
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-yellow-500/15 flex items-center justify-center shrink-0">
                        <Star className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-semibold text-foreground text-sm">
                            Évaluation disponible · <span className="text-muted-foreground font-normal truncate">{addressLabel}</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Prenez 30 secondes pour noter le suivi de <strong className="text-foreground">{agentName}</strong>. Vos retours rythment la vente.
                        </p>
                        <button
                            onClick={() => onEvaluate?.(mandate)}
                            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-yellow-500 to-[#FF914D] text-white text-xs font-semibold hover:opacity-90 transition-opacity"
                            data-testid={`next-evaluation-cta${testIdSuffix}`}
                        >
                            <Star className="w-3.5 h-3.5" />
                            Évaluer {agentName}
                        </button>
                    </div>
                </div>
                {sparkline}
            </div>
        );
    }

    const subtitle =
        donePeriod === period && period >= 1
            ? 'Merci, votre dernière évaluation est enregistrée.'
            : 'Vous serez notifié par email le moment venu — rien à faire d\u2019ici là.';

    return (
        <div
            className="bg-card rounded-xl border border-border p-5"
            data-testid={`next-evaluation-card${testIdSuffix}`}
        >
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#681793]/15 flex items-center justify-center shrink-0">
                    {donePeriod === period && period >= 1 ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    ) : (
                        <CalendarClock className="w-5 h-5 text-[#A855F7]" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground text-sm">
                        Prochaine évaluation · <span className="text-muted-foreground font-normal truncate">{addressLabel}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {subtitle} <span className="text-foreground/70">Agent suivi : {agentName}.</span>
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                        <span className="text-lg font-bold text-foreground">
                            {daysUntil === 0 ? "aujourd'hui" : `dans ${daysUntil} j`}
                        </span>
                        <span className="text-[11px] text-muted-foreground">· {dateLabel}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <BellRing className="w-3 h-3 text-[#FF914D]" />
                        Rappel automatique par email
                    </div>
                </div>
            </div>
            {sparkline}
        </div>
    );
}

/**
 * Carte "Prochaine évaluation" — point d'entrée multi-mandats.
 *
 * Affiche UNE carte par mandat actif du vendeur. Chaque carte cible son agent
 * propre (utile quand le vendeur a plusieurs biens chez différents agents, ou
 * un mandat en exclu avec un agent + un mandat simple avec un autre).
 */
export function NextEvaluationCard({ mandates, onEvaluate }) {
    const activeMandates = (mandates || [])
        .filter((m) => !SELLER_INACTIVE.includes(m.status))
        .slice()
        .sort(
            (a, b) =>
                new Date(a.signed_at || a.start_date || a.created_at) -
                new Date(b.signed_at || b.start_date || b.created_at)
        );

    if (activeMandates.length === 0) return null;

    return (
        <div className="space-y-3" data-testid="next-evaluation-stack">
            {activeMandates.map((m) => (
                <SingleMandateEvalCard key={m.id} mandate={m} onEvaluate={onEvaluate} />
            ))}
        </div>
    );
}

export default NextEvaluationCard;
